import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, PenLine, RefreshCw } from 'lucide-react';
import styled from 'styled-components';
import { Button, Card, CardTitle, Empty, Message } from '../admin/ui';
import * as Api from './EmailAdminApi';
import {
  errorText, groupLabel, recipientLabel, templateGroups, templateName, templateWhen,
} from './emailAdminModel';

// The "Emails" view of the E-Mails screen: every template the system sends, grouped by the backend's
// MailGroup and in its order, one row per key. A row says who receives the email and when it goes
// out, whether it is the built-in default or an edited version (and who last saved it), and marks
// the two kinds the owner called out: account emails (a broken edit can lock somebody out) and
// direct-action emails (they can also be edited for one send). The row opens the editor.
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .75rem 1rem;
  align-items: flex-start;
  padding: .8rem 0;
  border-top: 1px solid #eef2f7;

  &:first-of-type {
    border-top: 0;
  }
`;

const RowText = styled.div`
  flex: 1 1 22rem;
  min-width: 0;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .4rem;
  margin-top: .35rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .12rem .45rem;
  font-size: .7rem;
  font-weight: 700;
  background: ${(p) => (p.$warn ? '#fef3c7' : '#e0f2fe')};
  color: ${(p) => (p.$warn ? '#92400e' : '#075985')};
`;

const Meta = styled.div`
  margin-top: .2rem;
  font-size: .78rem;
  color: ${(p) => p.$muted ? '#64748b' : '#334155'};
  line-height: 1.45;
`;

const EmailTemplates = ({ onOpen }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    setTemplates(null);
    Api.fetchTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch(setError);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <Card>
        <Message error role="alert">
          {errorText(error, t('emailAdmin.templates.loadFailed', 'Could not load the email templates.'))}
        </Message>
        <Button type="button" secondary onClick={load}>
          <RefreshCw size={16} /><FormattedMessage id="emailAdmin.refresh" defaultMessage="Refresh" />
        </Button>
      </Card>
    );
  }
  if (!templates) {
    return <Card><Empty><FormattedMessage id="emailAdmin.templates.loading" defaultMessage="Loading templates…" /></Empty></Card>;
  }
  if (templates.length === 0) {
    return <Card><Empty><FormattedMessage id="emailAdmin.templates.empty" defaultMessage="There is no email template." /></Empty></Card>;
  }

  return (
    <>
      <Card>
        <p style={{ margin: '0 0 1rem', fontSize: '.82rem', color: '#64748b' }}>
          <FormattedMessage id="emailAdmin.templates.intro"
            defaultMessage="Every email the system sends. Open one to edit its subject and text, or to preview the next email with sample data." />
        </p>
        {templateGroups(templates).map(({ group, items }) => (
          <section key={group} aria-label={groupLabel(t, group)} style={{ marginBottom: '1.25rem' }}>
            <CardTitle style={{ fontSize: '.95rem', margin: '0 0 .25rem' }}>{groupLabel(t, group)}</CardTitle>
            {items.map((template) => (
              <Row key={template.key}>
                <RowText>
                  <div style={{ fontWeight: 700, color: '#111D4E', overflowWrap: 'break-word' }}>
                    {templateName(t, template)}
                  </div>
                  <Meta>
                    <FormattedMessage id="emailAdmin.template.to" defaultMessage="To: {recipient}"
                      values={{ recipient: recipientLabel(t, template.recipient) }} />
                  </Meta>
                  <Meta $muted>{templateWhen(t, template)}</Meta>
                  <Tags>
                    {template.accountWarning && (
                      <Tag $warn title={t('emailAdmin.template.accountWarning', 'A broken template can stop people from logging in, signing up or confirming their address.')}>
                        <AlertTriangle size={12} />
                        <FormattedMessage id="emailAdmin.template.accountBadge" defaultMessage="Can block sign-in" />
                      </Tag>
                    )}
                    {template.directAction && (
                      <Tag title={t('emailAdmin.template.directActionHint', 'This email can also be edited once when it is sent.')}>
                        <FormattedMessage id="emailAdmin.template.directAction" defaultMessage="Can be edited once when sending" />
                      </Tag>
                    )}
                  </Tags>
                  <Meta $muted>
                    {template.isDefault
                      ? <FormattedMessage id="emailAdmin.template.default" defaultMessage="Built-in default" />
                      : (
                        <FormattedMessage id="emailAdmin.template.edited"
                          defaultMessage="Edited {date}{by, select, none {} other { by {by}}}"
                          values={{
                            date: template.updatedAt
                              ? intl.formatDate(new Date(template.updatedAt), { dateStyle: 'medium', timeStyle: 'short' })
                              : '',
                            by: template.updatedBy || 'none',
                          }} />
                      )}
                  </Meta>
                </RowText>
                <Button type="button" secondary
                  aria-label={t('emailAdmin.template.editAria', 'Edit {name}', { name: templateName(t, template) })}
                  onClick={() => onOpen(template.key)}>
                  <PenLine size={15} /><FormattedMessage id="emailAdmin.template.edit" defaultMessage="Edit" />
                </Button>
              </Row>
            ))}
          </section>
        ))}
      </Card>
    </>
  );
};

export default EmailTemplates;
